/**
 * SigningSurfaceDispatcher — per-signer / per-doc engine + capture-mode router.
 *
 * Bill 2026-05-10 — Phase 1 (v0.14.17 bundle).
 *
 * Lifecycle:
 *   1. On mount, ask the backend routing-table which engine (internal /
 *      signnow / wet-attested) should fire for each (signer × doc) pair
 *      given the cart's cohortKey + the surface we're rendering on
 *      (in-branch / online-cart / rcp-remote / wet-attested).
 *   2. Group decisions into a flat plan and walk through it one
 *      (signer × doc) at a time so the FSR (or the customer on mobile)
 *      sees a single capture surface at any moment.
 *   3. Before any internal-engine signature can land, gate the signer
 *      through `<EsignConsentGate>` — once per signer per session.
 *   4. For each pair: render the right primitive:
 *        engine='internal' + captureMode='click' → <ClickToSignCapture>
 *        engine='internal' + captureMode='typed' → <TypedSignatureCapture>
 *        engine='internal' + captureMode='drawn' → drawn panel
 *          (mobile-fallback QR when isTouchDevice() is false)
 *        engine='signnow' + surface='online-cart' → <EmbeddedSign> reuse
 *        engine='signnow' + surface='rcp-remote'  → "invite sent" panel
 *        engine='wet-attested'                    → <WetSignScanner>
 *   5. After every pair completes, call the (sibling-owned) internal
 *      capture endpoint to write the SignedDocumentAudit row.
 *   6. When every internal-engine pair has captured, call the (sibling-
 *      owned) `internal.complete` endpoint to flip the SigningSession
 *      status. SignNow pairs flip via webhook (already wired in v0.14.x).
 *
 * Q1 capture-mode tiering is encoded in `resolveCaptureMode` below so the
 * dispatcher can fall back gracefully when the backend routing endpoint
 * isn't reachable.
 *
 * Plain-language doctrine — every label / button / banner uses customer-
 * facing English with no backend names.
 *
 * CONTRACT(sibling-backend-core agent):
 *   - POST /v3/config/signing-engine-routing/choose-per-signer
 *       body  { cartId, signerIndex, cohortKey, docTypeCode, surface }
 *       reply { engine: 'internal'|'signnow'|'wet-attested', captureMode? }
 *     Wrapped as `client.retail.signing.routing.choosePerSignerEngine`.
 *     Until that lands, this dispatcher falls back to a local table
 *     mirroring memory Q1 + Q3 + Q10 (see chooseEngineLocal).
 *
 * LANDED (punch-list S3.1, 2026-06-11):
 *   - POST /v3/signing/internal/complete  body { cartId }
 *     Wrapped as `client.retail.signing.internal.complete` (typed). The
 *     dispatcher calls it firmly once every plan row is captured AND the
 *     plan contains internal-engine rows with no SignNow rows (SignNow
 *     rows advance optimistically in this UI before the customer actually
 *     signs — the SignNow webhook owns session completion for those).
 *     Failures are non-fatal but logged loudly: the SigningSession stays
 *     open until a sweeper / retry closes it, so the pipeline resume
 *     (AcctAdd etc.) does NOT fire until complete succeeds.
 *
 * CONTRACT(sibling-primitives agent):
 *   - `<DrawnSignatureCapture>` (canvas + Pointer Events). Not yet on disk
 *     at v0.14.17 cut. While it's missing, the dispatcher renders an
 *     inline "open on your phone" panel + QR — same UX intent. Swap is
 *     a one-line import + JSX once primitives ships it.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import type {
  SignatureCaptureMode,
  SignatureCaptureResult,
} from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import {
  ClickToSignCapture,
  DisclosureReviewCapture,
  TypedSignatureCapture,
  DrawnSignatureCapture,
} from '@retail-ops/signing-capture';
import { PdfCanvasViewer } from '@/components/common/PdfCanvasViewer';
import { EsignConsentGate } from './EsignConsentGate';
import { WetSignScanner, type WetSignScanSummary } from './WetSignScanner';
import { isTouchDevice } from './touchDetect';
import { generateQrSvg } from './qrCode';

// ── Public types ───────────────────────────────────────────────────────────

export type SigningSurface = 'in-branch' | 'online-cart' | 'rcp-remote' | 'wet-attested';

export type SigningEngine = 'internal' | 'signnow' | 'wet-attested';

export interface SignerInfo {
  /** 0-based position in the cart's signer list. */
  signerIndex: number;
  /** Display name shown in capture surfaces. */
  displayName: string;
  /** First name — used by TypedSignatureCapture prefill. */
  firstName?: string;
  /** Last name — used by TypedSignatureCapture prefill. */
  lastName?: string;
  /** Email — used by ESIGN audit + SignNow invite. */
  email: string;
  /** Optional phone — used by SignNow SMS delivery. */
  phone?: string;
  /** Required for ESIGN consent + internal capture writes. */
  cifNumber: string;
}

export interface DocInfo {
  /**
   * Stable per-cart instance id (SignedDocumentAudit join key).
   * 2026-07-01: MUST be the SERVER's instance id from
   * /v3/signing/internal/prepare — the server's capture endpoint only
   * accepts ids from the package IT rendered. A client-generated UUID
   * fails DOCUMENT_NOT_IN_PACKAGE.
   */
  documentInstanceId: string;
  /** Canonical doc-type code — drives routing + Q1 tiering. */
  docTypeCode: string;
  /** Plain-English title shown to the customer in the headline. */
  documentTitle: string;
  /**
   * Server-resolved capture mode (prepare's acceptableCaptureMode). When
   * present it wins over the local Q1 tier map — the server is authoritative.
   */
  captureMode?: SignatureCaptureMode;
  /**
   * False = view-only disclosure (Bill 2026-09-30). Presented as a 'review'
   * row: shown, acknowledged, never signed, never routed to an engine.
   */
  requiresSignature?: boolean;
}

export interface SigningSurfaceDispatcherProps {
  cartId: string;
  signers: ReadonlyArray<SignerInfo>;
  docs: ReadonlyArray<DocInfo>;
  surface: SigningSurface;
  /** Locked entity cohort key (e.g. 'trust-revocable', 'business-llc'). */
  cohortKey: string;
  /**
   * Render hint — when true, hide the Teams chrome wrappers and render
   * mobile-first. Used by the /sign-mobile route.
   */
  isTouchOnly?: boolean;
  /** Optional starting signer (mobile fallback resumes mid-flow). */
  startSignerIndex?: number;
  /** Restrict the plan to drawn-mode docs only — used on mobile fallback. */
  drawnOnly?: boolean;
  /** Skip ESIGN gate (the desktop session already captured it). */
  skipEsignGate?: boolean;
  /**
   * Phase A (Bill 2026-07-07) — how the in-branch ceremony presents.
   * 'kiosk-default' auto-opens the signer display (external browser window
   * on the customer monitor; QR panel is the fallback); 'kiosk-optional'
   * keeps the manual "Open signer display" button; 'fsr-inline' (or absent)
   * keeps the pre-Phase-A in-app flow.
   */
  presentationMode?: 'fsr-inline' | 'kiosk-optional' | 'kiosk-default';
  /** Customer-facing signer surface base URL — token appended as ?t=. */
  signerPortalBaseUrl?: string;
  /** Called after all (signer × doc) pairs in the plan complete. */
  onAllComplete?: () => void;
  /**
   * BACKLOG-1482 — the outcome of closing the signing session (internal.complete),
   * which is what starts the account-opening pipeline. It used to be reported to
   * console.error only; in Teams there is no console, so a failed close looked
   * like success. Fires before onAllComplete: ok=false means the accounts will
   * NOT open until it is retried. 'skipped' is the mixed SignNow plan, where the
   * webhook closes the session.
   */
  onCompleteResult?: (result: { ok: boolean; skipped?: boolean; message?: string }) => void;
  /** Called when the FSR / customer cancels mid-flow. */
  onCancel?: () => void;
  /** Officer short code — surfaced to the wet-sign + SignNow embedded surfaces. */
  officerCode?: string;
  /**
   * Per-signer capture preference (signerIndex → 'drawn' | 'typed'), from the
   * FSR's Configure-Signing-Session plan (Bill 2026-07-08). Applied ONLY to
   * signature docs (captureMode 'drawn'/'typed'); click/consent docs are never
   * overridden. Absent → unchanged default (server / Q1 tiering) behavior.
   */
  signerCapturePrefs?: ReadonlyMap<number, 'drawn' | 'typed'>;
  /** Signers who review the no-signature disclosures (server prepare, Bill 2026-10-15). Absent =
   *  every signer reviews them. */
  reviewSignerIndexes?: ReadonlySet<number>;
}

// ── Internal types ─────────────────────────────────────────────────────────

interface PlanRow {
  signer: SignerInfo;
  doc: DocInfo;
  engine: SigningEngine;
  captureMode: SignatureCaptureMode;
}

// ── Q1 capture-mode tiering (fallback / starting point) ────────────────────

const CLICK_DOC_CODES = new Set<string>([
  'ESIGN_CONSENT',
  'REG_E_OPT_IN',
  'REG_E_OPT_OUT',
  'OVERDRAFT_OPT_IN',
  'ESTATEMENTS_OPT_IN',
  'MARKETING_OPT_OUT',
  'PAPERLESS_CONSENT',
]);

const DRAWN_DOC_CODES = new Set<string>([
  'SIGNATURE_CARD',
  'BANKING_RESOLUTION',
  'BO_CERT',
  'TRUSTEE_CERT',
  'TRUST_CERT',
]);

/**
 * Q1 doc-role tiering. Pure function — no I/O. Fed into the dispatcher
 * both as a fallback (when backend routing is unreachable) and as a
 * post-routing sanity check (the backend can override per-cohort).
 */
export function resolveCaptureMode(docTypeCode: string): SignatureCaptureMode {
  if (CLICK_DOC_CODES.has(docTypeCode)) return 'click';
  if (DRAWN_DOC_CODES.has(docTypeCode)) return 'drawn';
  return 'typed';
}

// ── Q3 + Q10 local engine table (fallback when backend routing missing) ────

interface LocalRoutingRow {
  cohortPrefix: string; // matches the start of cohortKey
  docTypeCode: string;
  surfaces: ReadonlyArray<SigningSurface | '*'>;
  engine: SigningEngine;
}

const LOCAL_ROUTING: ReadonlyArray<LocalRoutingRow> = [
  // Q10 — Trustee Cert
  { cohortPrefix: 'trust-revocable', docTypeCode: 'TRUSTEE_CERT', surfaces: ['in-branch'], engine: 'internal' },
  { cohortPrefix: 'trust-revocable', docTypeCode: 'TRUSTEE_CERT', surfaces: ['wet-attested'], engine: 'wet-attested' },
  { cohortPrefix: 'trust-revocable', docTypeCode: 'TRUSTEE_CERT', surfaces: ['online-cart', 'rcp-remote'], engine: 'signnow' },
  { cohortPrefix: 'trust-irrevocable', docTypeCode: 'TRUSTEE_CERT', surfaces: ['*'], engine: 'signnow' },
  { cohortPrefix: 'trust-self-settled-snt', docTypeCode: 'TRUSTEE_CERT', surfaces: ['*'], engine: 'signnow' },
  // Q1 click-tier docs route to internal regardless of surface (low stakes).
  { cohortPrefix: '', docTypeCode: 'ESIGN_CONSENT', surfaces: ['*'], engine: 'internal' },
  { cohortPrefix: '', docTypeCode: 'REG_E_OPT_IN', surfaces: ['*'], engine: 'internal' },
  { cohortPrefix: '', docTypeCode: 'REG_E_OPT_OUT', surfaces: ['*'], engine: 'internal' },
  { cohortPrefix: '', docTypeCode: 'ESTATEMENTS_OPT_IN', surfaces: ['*'], engine: 'internal' },
  { cohortPrefix: '', docTypeCode: 'PAPERLESS_CONSENT', surfaces: ['*'], engine: 'internal' },
  { cohortPrefix: '', docTypeCode: 'MARKETING_OPT_OUT', surfaces: ['*'], engine: 'internal' },
];

function chooseEngineLocal(args: {
  cohortKey: string;
  docTypeCode: string;
  surface: SigningSurface;
}): SigningEngine {
  const matches = LOCAL_ROUTING.filter(
    (r) =>
      (r.cohortPrefix === '' || args.cohortKey.startsWith(r.cohortPrefix)) &&
      r.docTypeCode === args.docTypeCode &&
      (r.surfaces.includes('*') || r.surfaces.includes(args.surface))
  );
  if (matches.length > 0) {
    // Prefer the most-specific cohort match (longest prefix).
    matches.sort((a, b) => b.cohortPrefix.length - a.cohortPrefix.length);
    return matches[0]!.engine;
  }
  // Defaults when no explicit routing row matched:
  //  - wet-attested surface           → wet-attested scan
  //  - in-branch (Phase 1 — the FSR + customer are AT THE DESK) → INTERNAL,
  //    in-person capture. We must NEVER silently email a remote SignNow link
  //    for an in-branch opening (Bill 2026-06-25). Remote is an ad-hoc switch
  //    the FSR chooses per (signer × doc), not a default.
  //  - online-cart / rcp-remote (customer not present) → signnow
  if (args.surface === 'wet-attested') return 'wet-attested';
  if (args.surface === 'in-branch') return 'internal';
  return 'signnow';
}

// ── Backend routing wrapper with graceful fallback ─────────────────────────

interface ChooseArgs {
  cartId: string;
  signerIndex: number;
  cohortKey: string;
  docTypeCode: string;
  surface: SigningSurface;
}

interface ChooseReply {
  engine: SigningEngine;
  captureMode?: SignatureCaptureMode;
}

// CONTRACT(sibling-backend-core agent): the api-client doesn't yet expose
// `client.retail.signing.routing.choosePerSignerEngine`. When the wrapper lands,
// swap this fetch for the typed call. Until then we POST directly + fall
// back to the local routing table on any error.
async function fetchEnginePlan(
  apiClient: ReturnType<typeof useApiClient>,
  args: ChooseArgs
): Promise<ChooseReply> {
  try {
    // Cast through unknown — the api-client client object is broad enough
    // that the typed-routing namespace may already be partially present.
    type RoutingShape = {
      retail?: { signing?: { routing?: { choosePerSignerEngine?: (a: ChooseArgs) => Promise<ChooseReply> } } };
    };
    const maybeRouting =
      (apiClient as unknown as RoutingShape).retail?.signing?.routing?.choosePerSignerEngine;
    if (typeof maybeRouting === 'function') {
      const r = await maybeRouting(args);
      return r;
    }
  } catch {
    // fall through
  }
  // Local fallback.
  return {
    engine: chooseEngineLocal({
      cohortKey: args.cohortKey,
      docTypeCode: args.docTypeCode,
      surface: args.surface,
    }),
    captureMode: resolveCaptureMode(args.docTypeCode),
  };
}

// ── Styles ─────────────────────────────────────────────────────────────────

const containerS: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  fontFamily: "'Roboto', system-ui, -apple-system, 'Segoe UI', sans-serif",
  color: 'var(--ink-900)',
};

const progressBarTrackS: CSSProperties = {
  height: 6,
  background: 'var(--ink-200)',
  borderRadius: 999,
  overflow: 'hidden',
};

const progressBarFillS = (pct: number): CSSProperties => ({
  width: `${pct}%`,
  height: '100%',
  background: 'var(--brand-fill)',
  transition: 'width 200ms ease-out',
});

const progressLabelS: CSSProperties = {
  marginTop: 6,
  fontSize: 12,
  color: 'var(--ink-700)',
};

const bannerS = (tone: 'info' | 'warn' | 'err' | 'ok'): CSSProperties => {
  const palettes: Record<'info' | 'warn' | 'err' | 'ok', { bg: string; fg: string; bd: string }> = {
    info: { bg: 'var(--info-bg)', fg: 'var(--pcb-blue-dark)', bd: 'var(--info-line)' },
    warn: { bg: 'var(--warn-bg)', fg: 'var(--warn-text)', bd: 'var(--warn-line)' },
    err: { bg: 'var(--err-bg)', fg: 'var(--err-text)', bd: 'var(--err-line)' },
    ok: { bg: 'var(--ok-bg)', fg: 'var(--ok-text)', bd: 'var(--ok-line)' },
  };
  const p = palettes[tone];
  return {
    background: p.bg,
    color: p.fg,
    border: `1px solid ${p.bd}`,
    padding: '12px 16px',
    borderRadius: 8,
    fontSize: 13,
    lineHeight: 1.5,
  };
};

const cardS: CSSProperties = {
  background: 'var(--surface-card)',
  borderRadius: 10,
  border: '1px solid var(--surface-border)',
  padding: 20,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.04)',
};

const headingS: CSSProperties = {
  margin: 0,
  fontSize: 16,
  fontWeight: 600,
  color: 'var(--pcb-blue-dark)',
};

const subHeadS: CSSProperties = {
  margin: 0,
  fontSize: 13,
  color: 'var(--ink-700)',
};

const qrWrapS: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 10,
  padding: '12px 0',
};

const qrLinkS: CSSProperties = {
  fontSize: 12,
  color: 'var(--pcb-blue)',
  textDecoration: 'underline',
  wordBreak: 'break-all',
};

const ghostBtnS: CSSProperties = {
  padding: '8px 14px',
  borderRadius: 6,
  border: '1px solid var(--surface-border)',
  background: 'var(--surface-card)',
  color: 'var(--ink-900)',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const primaryBtnS: CSSProperties = {
  padding: '10px 18px',
  borderRadius: 6,
  border: '1px solid var(--pcb-blue)',
  background: 'var(--brand-fill)',
  color: 'var(--on-brand)',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

// ── Component ──────────────────────────────────────────────────────────────

export function SigningSurfaceDispatcher(
  props: SigningSurfaceDispatcherProps
): JSX.Element {
  const {
    cartId,
    signers,
    docs,
    surface,
    cohortKey,
    isTouchOnly,
    startSignerIndex,
    drawnOnly,
    skipEsignGate,
    presentationMode,
    signerPortalBaseUrl,
    onAllComplete,
    onCompleteResult,
    onCancel,
    officerCode,
    signerCapturePrefs,
    reviewSignerIndexes,
  } = props;

  const apiClient = useApiClient();

  // ── Plan state ──────────────────────────────────────────────────────────
  const [plan, setPlan] = useState<ReadonlyArray<PlanRow> | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  const [planLoading, setPlanLoading] = useState<boolean>(true);

  // ── Walk-state ──────────────────────────────────────────────────────────
  // Index into `plan`. ESIGN gate flips per-signer outside this index.
  const [cursor, setCursor] = useState<number>(0);
  // BACKLOG-1434 — a server-side refusal (zero presentable documents, or a step the
  // server declined) must STOP the ceremony visibly instead of polling forever.
  const [blockedReason, setBlockedReason] = useState<string | null>(null);
  const [completed, setCompleted] = useState<Set<number>>(new Set());
  const [esignPassed, setEsignPassed] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [mobileLinkUrl, setMobileLinkUrl] = useState<string | null>(null);

  // ── Signer display (cross-device, Bill 2026-06-24) ──────────────────────
  // When the signer display is open, internal captures route to the
  // customer-facing kiosk (the cloned customer monitor / signer portal)
  // instead of rendering inline here. Phase A (Bill 2026-07-07): with
  // presentationMode='kiosk-default' it AUTO-opens (external browser window
  // + QR fallback) and the E-Sign consent runs on the kiosk too;
  // 'kiosk-optional'/'fsr-inline' keep the manual "Open signer display" path.
  const [signerDisplay, setSignerDisplay] = useState<
    { presentationId: string; token: string; pairCode: string } | null
  >(null);
  const [signerUrlQr, setSignerUrlQr] = useState<string | null>(null);
  const [openingDisplay, setOpeningDisplay] = useState<boolean>(false);
  // BACKLOG-0875 — reconnect a dropped signer: reissue a fresh token for the
  // SAME presentation (same cart/step). `reissuing` guards the button;
  // `reissuedAt` briefly acks the swap so the FSR knows the old link is dead.
  const [reissuing, setReissuing] = useState<boolean>(false);
  const [reissueNote, setReissueNote] = useState<string | null>(null);

  // Phase A (Bill 2026-07-07) — signer URL comes from the server's prepare
  // response, never hardcoded. Token appended as ?t=.
  const signerPortalBase = signerPortalBaseUrl ?? 'https://portal.mypcb.com/dsp/sign';
  const buildSignerUrl = useCallback(
    (token: string) => `${signerPortalBase}?t=${encodeURIComponent(token)}`,
    [signerPortalBase],
  );
  // Host+path shown in the FSR instruction text.
  const signerPortalDisplay = useMemo(() => {
    try {
      const u = new URL(signerPortalBase);
      return `${u.host}${u.pathname}`;
    } catch {
      return signerPortalBase;
    }
  }, [signerPortalBase]);
  // External kiosk browser window (kiosk-default auto-open). window.open from
  // Teams desktop routes through the external-link handler → system browser
  // (LN-popup precedent). null = popup blocked; the QR panel covers pairing.
  const signerWindowRef = useRef<Window | null>(null);

  // Stable content-key for the (signer × doc) set. The plan effect keys on THIS
  // rather than the `signers`/`docs` array identities, so a benign re-render that
  // hands us content-identical arrays (e.g. the parent SignStep rebuilds its
  // disclosure package on a cart reload) does NOT rebuild the plan and reset the
  // capture cursor to 0 mid-signing. Only a real change to the signer set or the
  // document set re-plans.
  const planKey = useMemo(
    () =>
      signers.map((s) => `${s.signerIndex}:${s.cifNumber}`).join('|') +
      '##' +
      docs.map((d) => `${d.documentInstanceId}:${d.docTypeCode}`).join('|') +
      '##' +
      (signerCapturePrefs
        ? [...signerCapturePrefs.entries()].map(([k, v]) => `${k}=${v}`).sort().join(',')
        : '') +
      '##' +
      (reviewSignerIndexes ? [...reviewSignerIndexes].sort().join(',') : '*'),
    [signers, docs, signerCapturePrefs, reviewSignerIndexes],
  );

  // ── Mount: ask backend routing for every (signer × doc) pair ────────────
  useEffect(() => {
    let cancelled = false;
    setPlanLoading(true);
    setPlanError(null);
    (async () => {
      const rows: PlanRow[] = [];
      try {
        for (const signer of signers) {
          for (const doc of docs) {
            // View-only disclosures (Bill 2026-09-30): no engine routing, no
            // capture preference — every signer reviews every one, in-house.
            if (doc.requiresSignature === false) {
              // Only the signers whose review strategy isn't 'hidden' (their delivery choice can
              // decide it, Bill 2026-10-15); no list from the server = everyone, as before.
              if (!reviewSignerIndexes || reviewSignerIndexes.has(signer.signerIndex)) {
                rows.push({ signer, doc, engine: 'internal', captureMode: 'review' });
              }
              continue;
            }
            const reply = await fetchEnginePlan(apiClient, {
              cartId,
              signerIndex: signer.signerIndex,
              cohortKey,
              docTypeCode: doc.docTypeCode,
              surface,
            });
            // Server-resolved mode (prepare's acceptableCaptureMode) wins;
            // routing reply + local Q1 tiering are fallbacks.
            const baseCaptureMode =
              doc.captureMode ?? reply.captureMode ?? resolveCaptureMode(doc.docTypeCode);
            // FSR per-signer capture preference (Bill 2026-07-08) overrides ONLY
            // signature docs (drawn/typed). Click/consent docs keep their mode.
            const pref = signerCapturePrefs?.get(signer.signerIndex);
            const chosen =
              (baseCaptureMode === 'drawn' || baseCaptureMode === 'typed') && pref
                ? pref
                : baseCaptureMode;
            // CHG-0096 (Bill 2026-10-26): in branch a signature is always drawn. A typed preference saved before the
            // fix (or a typed mode from anywhere) would push a step the server refuses, leaving the signer stuck.
            const captureMode = surface === 'in-branch' && chosen === 'typed' ? 'drawn' : chosen;
            rows.push({ signer, doc, engine: reply.engine, captureMode });
          }
        }
        // Bill 2026-10-14 (Collette, Gayle Test): plan only the signer x document pairs the
        // server assigns. Planning every signer onto every signature document gave a joint
        // owner who is not the TIN holder a turn on the savings signature card; the server had
        // nothing for her to sign and the panel froze. Review (disclosure) steps stay for every
        // signer. If the server can't answer, keep the old plan (over-ask, never under-ask).
        let assigned: Readonly<Record<string, ReadonlyArray<number>>> | null = null;
        try {
          const a = await apiClient.retail.signing.presentation.assignments({
            cartId,
            signers: signers.map((sg) => ({ signerIndex: sg.signerIndex, cifNumber: sg.cifNumber ?? null })),
          });
          if (a.success && a.assignments) assigned = a.assignments;
        } catch {
          /* older server or transient - keep the full plan */
        }
        let filtered = assigned
          ? rows.filter((r) => {
              if (r.captureMode === 'review') return true;
              const who = assigned![r.doc.documentInstanceId];
              return who === undefined || who.includes(r.signer.signerIndex);
            })
          : rows;
        if (drawnOnly) {
          filtered = rows.filter((r) => r.engine === 'internal' && r.captureMode === 'drawn');
        }
        if (typeof startSignerIndex === 'number') {
          filtered = filtered.filter((r) => r.signer.signerIndex >= startSignerIndex);
        }
        if (!cancelled) {
          setPlan(filtered);
          setCursor(0);
        }
      } catch (err) {
        if (!cancelled) {
          setPlanError(err instanceof Error ? err.message : 'Could not build the signing plan.');
        }
      } finally {
        if (!cancelled) setPlanLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // signers/docs are read in the body but intentionally NOT deps — planKey
    // captures their CONTENT, so a content-identical rebuild doesn't re-plan +
    // reset the cursor. Only a real signer/doc change re-runs this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClient, cartId, planKey, cohortKey, surface, drawnOnly, startSignerIndex]);

  // ── Build a tokenized mobile-fallback link once we know the cart ────────
  // The token is minted server-side; until that endpoint exists we render a
  // best-effort URL with the cartId + signerIndex so an FSR can hand-key it.
  //
  // CONTRACT(sibling-backend-core agent): when /v3/signing/internal/mint-
  // mobile-token lands, swap the URL builder below for the typed call.
  useEffect(() => {
    if (isTouchOnly) return; // mobile route doesn't need its own QR
    if (!plan) return;
    const currentRow = plan[cursor];
    if (!currentRow) return;
    if (currentRow.engine !== 'internal' || currentRow.captureMode !== 'drawn') return;
    if (isTouchDevice()) return; // device already supports drawn capture
    const url =
      `${typeof window !== 'undefined' ? window.location.origin : ''}` +
      `/sign-mobile?cartId=${encodeURIComponent(cartId)}` +
      `&signerIndex=${encodeURIComponent(String(currentRow.signer.signerIndex))}`;
    setMobileLinkUrl(url);
    let cancelled = false;
    (async () => {
      try {
        const svg = await generateQrSvg(url, { size: 180 });
        if (!cancelled) setQrSvg(svg);
      } catch {
        if (!cancelled) setQrSvg(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [plan, cursor, cartId, isTouchOnly]);

  // ── Review rows: the document itself, fetched for the in-branch screen ───
  // The kiosk renders the PDF from its own token route; on the FSR's screen
  // (fsr-inline, or alongside the kiosk) the same flattened bytes are drawn by
  // pdf.js (PdfCanvasViewer; CHG-0097 — an <iframe> PDF is blank on bank PCs).
  const currentRowForDoc = plan?.[cursor];
  const [reviewDocBlob, setReviewDocBlob] = useState<Blob | null>(null);
  const [reviewDocError, setReviewDocError] = useState<string | null>(null);
  useEffect(() => {
    if (!currentRowForDoc || currentRowForDoc.captureMode !== 'review') {
      setReviewDocBlob(null);
      setReviewDocError(null);
      return;
    }
    let cancelled = false;
    setReviewDocBlob(null);
    setReviewDocError(null);
    (async () => {
      try {
        const blob = await apiClient.signing.getOfficerDocumentBlob(cartId, currentRowForDoc.doc.documentInstanceId);
        if (!cancelled) setReviewDocBlob(blob);
      } catch (err) {
        if (!cancelled) setReviewDocError(err instanceof Error ? err.message : 'The disclosure could not be loaded for display.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiClient, cartId, currentRowForDoc]);

  // ── Per-row capture submit ──────────────────────────────────────────────
  const submitCapture = useCallback(
    async (row: PlanRow, result: SignatureCaptureResult) => {
      if (!row.signer.cifNumber) {
        setError('Missing CIF number for this signer — cannot record signature.');
        return;
      }
      setSubmitting(true);
      setError(null);
      try {
        // 2026-07-01 — body mirrors the server's InternalSigningCaptureInput
        // exactly (required: documentId / intentTimestamp / captureMode). The
        // old {captureResult} envelope never deserialized server-side and
        // every capture 400'd. clickAttestation reconstructs the exact
        // sentence ClickToSignCapture displayed, so the audit row records the
        // words the customer accepted verbatim.
        const clickAttestation =
          result.mode === 'click'
            ? `I, ${row.signer.displayName}, confirm this is my electronic signature for ${row.doc.documentTitle}.` +
              (result.initials ? ` Initials: ${result.initials}.` : '')
            : result.mode === 'review'
              // The acknowledgement sentence the customer ticked, verbatim.
              ? result.acknowledgement
              : undefined;
        const resp = await apiClient.retail.signing.internal.capture({
          cartId,
          signerIndex: row.signer.signerIndex,
          documentId: row.doc.documentInstanceId,
          intentTimestamp: result.intentTimestamp,
          captureMode: result.mode,
          ...(result.typedName ? { typedName: result.typedName } : {}),
          ...(result.svgPath
            ? { canvasSvg: result.svgPath }
            : result.renderedSvg
              ? { canvasSvg: result.renderedSvg }
              : {}),
          ...(result.pngBase64 ? { canvasPngBase64: result.pngBase64 } : {}),
          ...(clickAttestation ? { clickAttestation } : {}),
          signerUserAgent: result.signerUserAgent,
          signerViewportSize: result.signerViewportSize,
          ...(typeof result.scrollProgressPct === 'number'
            ? { scrollProgressPct: result.scrollProgressPct }
            : {}),
          signerDisplayName: row.signer.displayName,
          signerCifNumber: row.signer.cifNumber,
          signerEmail: row.signer.email,
          // In-branch ceremony: the FSR verified photo ID at the desk.
          identityVerificationMethod: 'fsr-photo-id-check',
        });
        if (!resp.success) {
          setError(resp.error ?? 'Could not save signature.');
          return;
        }
        setCompleted((prev) => {
          const next = new Set(prev);
          next.add(cursor);
          return next;
        });
        setCursor((c) => c + 1);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not save signature.');
      } finally {
        setSubmitting(false);
      }
    },
    [apiClient, cartId, cursor]
  );

  // ── Detect "all done" ───────────────────────────────────────────────────
  // Punch-list S3.1 (2026-06-11): the duck-typed probe is gone — the
  // api-client now exposes `v3.signing.internal.complete` as a typed method
  // and we call it firmly. Guard: only fire when the plan actually contains
  // internal-engine rows and NO SignNow rows. SignNow rows advance
  // optimistically in this UI (the FSR clicks Continue before the customer
  // signs), so completing the shared SigningSession here would prematurely
  // resume the pipeline — the SignNow webhook owns completion for those.
  //
  // Phase A (Bill 2026-07-07) — the presentation flips to 'completed'
  // SERVER-side at ceremony end (the kiosk shows its "All done" screen).
  // Flag normal completion so the unmount teardown does NOT cancel it.
  const ceremonyCompletedRef = useRef(false);
  useEffect(() => {
    if (!plan || plan.length === 0) return;
    if (cursor < plan.length) return;
    ceremonyCompletedRef.current = true;
    (async () => {
      const hasInternal = plan.some((r) => r.engine === 'internal');
      const hasSignNow = plan.some((r) => r.engine === 'signnow');
      try {
        if (hasInternal && !hasSignNow) {
          const resp = await apiClient.retail.signing.internal.complete({ cartId });
          onCompleteResult?.(resp.success
            ? { ok: true }
            : { ok: false, message: resp.error ?? resp.code ?? undefined });
          if (!resp.success) {
            // Server said no (bad cart id, etc.) — surface loudly so the
            // stuck session is diagnosable, but don't block the FSR's flow.
            // eslint-disable-next-line no-console
            console.error(
              '[SigningSurfaceDispatcher] internal.complete REJECTED for cart',
              cartId,
              '— SigningSession stays open and the pipeline will NOT resume until it completes.',
              resp.error ?? resp.code ?? '(no detail)'
            );
          } else {
            // eslint-disable-next-line no-console
            console.info(
              '[SigningSurfaceDispatcher] internal.complete ok for cart',
              cartId,
              resp.data?.transitioned
                ? '(session flipped to completed; pipeline resume enqueued)'
                : '(idempotent no-op — session already terminal)'
            );
          }
        } else if (hasInternal && hasSignNow) {
          onCompleteResult?.({ ok: true, skipped: true });
          // eslint-disable-next-line no-console
          console.info(
            '[SigningSurfaceDispatcher] mixed plan (internal + SignNow) for cart',
            cartId,
            '— skipping internal.complete; the SignNow webhook closes the session.'
          );
        }
      } catch (err) {
        onCompleteResult?.({
          ok: false,
          message: err instanceof Error ? err.message : undefined,
        });
        // Non-fatal for the UI, but this is the CRITICAL return-path hook:
        // if it keeps failing the SigningSession never flips to 'completed'
        // and the post-sign pipeline (AcctAdd / CustRelAdd) never resumes.
        // eslint-disable-next-line no-console
        console.error(
          '[SigningSurfaceDispatcher] internal.complete FAILED for cart',
          cartId,
          '— SigningSession stays open; pipeline resume will not fire until a retry/sweeper completes it.',
          err
        );
      } finally {
        onAllComplete?.();
      }
    })();
  }, [plan, cursor, apiClient, cartId, onAllComplete, onCompleteResult]);

  // ── ESIGN gate handling ────────────────────────────────────────────────
  const currentRow: PlanRow | null = useMemo(() => {
    if (!plan) return null;
    if (cursor >= plan.length) return null;
    return plan[cursor] ?? null;
  }, [plan, cursor]);

  // Does the current signer have any internal-engine row remaining?
  const currentSignerNeedsEsign = useMemo(() => {
    if (!plan || !currentRow) return false;
    return plan.some(
      (r, i) => i >= cursor && r.signer.signerIndex === currentRow.signer.signerIndex && r.engine === 'internal'
    );
  }, [plan, cursor, currentRow]);

  const handleEsignPassed = useCallback(() => {
    if (!currentRow) return;
    setEsignPassed((prev) => {
      const next = new Set(prev);
      next.add(currentRow.signer.signerIndex);
      return next;
    });
  }, [currentRow]);

  // ── Renderers ───────────────────────────────────────────────────────────

  // ── Signer display: open / close + push-and-poll ────────────────────────
  const openSignerDisplay = useCallback(async () => {
    setError(null);
    setOpeningDisplay(true);
    try {
      const r = await apiClient.retail.signing.presentation.create({
        cartId,
        surface: 'in-branch',
      });
      if (r.success && r.presentationId && r.token) {
        setSignerDisplay({
          presentationId: r.presentationId,
          token: r.token,
          pairCode: r.pairCode ?? '',
        });
        if (presentationMode === 'kiosk-default') {
          // Auto-hand the ceremony to the customer monitor. Blocked popup
          // (null) fails silently — the QR panel renders regardless.
          signerWindowRef.current = window.open(
            buildSignerUrl(r.token),
            'pcb-signer-display',
            'popup,width=1100,height=850',
          );
        }
      } else {
        setError(r.error ?? 'Could not open the signer display.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open the signer display.');
    } finally {
      setOpeningDisplay(false);
    }
  }, [apiClient, cartId, presentationMode, buildSignerUrl]);

  const closeSignerDisplay = useCallback(async () => {
    const pid = signerDisplay?.presentationId;
    setSignerDisplay(null);
    setSignerUrlQr(null);
    setReissueNote(null);
    if (pid) {
      try {
        await apiClient.retail.signing.presentation.cancel(pid);
      } catch {
        /* best-effort */
      }
    }
  }, [apiClient, signerDisplay]);

  // BACKLOG-0875 — the signer's browser/kiosk page closed or lost its token.
  // Mint a FRESH capability token for the SAME presentation (same cart, same
  // pushed step) and re-show the QR/pairCode. The old ?t= link stops working.
  // If the kiosk auto-opened (kiosk-default), re-point the popup at the new URL.
  const reissueSignerLink = useCallback(async () => {
    const pid = signerDisplay?.presentationId;
    if (!pid) return;
    setReissuing(true);
    setReissueNote(null);
    setError(null);
    try {
      const r = await apiClient.retail.signing.presentation.reissue(pid);
      if (r.success && r.token) {
        setSignerDisplay({
          presentationId: r.presentationId ?? pid,
          token: r.token,
          pairCode: r.pairCode ?? '',
        });
        const url = buildSignerUrl(r.token);
        // Re-steer an already-open kiosk popup at the new token; a closed or
        // blocked popup (null/closed) just leaves the QR/pairCode to reconnect.
        try {
          if (signerWindowRef.current && !signerWindowRef.current.closed) {
            signerWindowRef.current.location.href = url;
          }
        } catch {
          /* cross-origin popup — customer rescans instead */
        }
        setReissueNote('New link issued — the old one no longer works. Rescan or re-enter the pairing code.');
      } else {
        setError(r.error ?? 'Could not reissue the signer link.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not reissue the signer link.');
    } finally {
      setReissuing(false);
    }
  }, [apiClient, signerDisplay, buildSignerUrl]);

  // Unmount teardown (2026-07-07) — the ceremony can end (onAllComplete →
  // SignStep unmounts this dispatcher) while a signer-display presentation is
  // still open server-side. Without this cleanup the presentation stayed
  // orphaned "open" and the UI hung in a phantom signing session. Ref-tracked
  // so the unmount closure cancels the CURRENT presentation, not a stale one.
  const signerDisplayRef = useRef(signerDisplay);
  signerDisplayRef.current = signerDisplay;
  useEffect(() => () => {
    const pid = signerDisplayRef.current?.presentationId;
    // Phase A — normal completion flips the presentation server-side;
    // cancelling here would kill the kiosk's "All done" screen. Only cancel
    // abandoned runs.
    if (pid && !ceremonyCompletedRef.current) {
      void apiClient.retail.signing.presentation.cancel(pid).catch(() => {
        /* best-effort teardown */
      });
    }
  }, [apiClient]);

  // kiosk-default (Phase A) — auto-open the signer display once per mount as
  // soon as the plan shows internal-engine work. Ref-guarded so re-renders
  // (and a failed create) don't re-fire; the FSR can still re-open manually.
  const autoOpenFiredRef = useRef(false);
  useEffect(() => {
    if (presentationMode !== 'kiosk-default') return;
    if (autoOpenFiredRef.current) return;
    if (signerDisplay) return;
    if (!plan || !plan.some((r) => r.engine === 'internal')) return;
    autoOpenFiredRef.current = true;
    void openSignerDisplay();
  }, [presentationMode, plan, signerDisplay, openSignerDisplay]);

  // QR for the signer URL — the FSR points the ASUS browser at it once.
  useEffect(() => {
    if (!signerDisplay) {
      setSignerUrlQr(null);
      return;
    }
    let cancelled = false;
    const url = buildSignerUrl(signerDisplay.token);
    (async () => {
      try {
        const svg = await generateQrSvg(url, { size: 190 });
        if (!cancelled) setSignerUrlQr(svg);
      } catch {
        if (!cancelled) setSignerUrlQr(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [signerDisplay, buildSignerUrl]);

  // Push the current kiosk step + poll for completion. Phase A (Bill
  // 2026-07-07): the E-Sign consent ceremony ALSO runs on the kiosk — the
  // per-signer ordering is consent step (if needed) → each signable doc step.
  // The kiosk records consent + captures server-side (same ESIGN_CONSENT
  // sentinel + >=80% scroll rule); we only advance here when
  // lastCapturedDocumentId round-trips the pushed documentId.
  useEffect(() => {
    if (!signerDisplay || !currentRow || currentRow.engine !== 'internal') return;
    if (!currentRow.signer.cifNumber) return;

    const pid = signerDisplay.presentationId;
    const signerIndex = currentRow.signer.signerIndex;
    const consentPending =
      !skipEsignGate && currentSignerNeedsEsign && !esignPassed.has(signerIndex);
    // Per-signer sentinel — the server records the consent row against it.
    const docId = consentPending
      ? `ESIGN_CONSENT#s${signerIndex}`
      : currentRow.doc.documentInstanceId;
    let cancelled = false;

    void apiClient.retail.signing.presentation.setStep(pid, {
      signerIndex,
      documentId: docId,
      captureMode: consentPending ? 'click' : currentRow.captureMode,
      // 'review' = a view-only disclosure the kiosk renders for acknowledgement.
      stepKind: consentPending ? 'consent' : currentRow.captureMode === 'review' ? 'review' : 'capture',
      docTitle: consentPending
        ? 'Consent for Electronic Transactions, Records, and Signatures'
        : currentRow.doc.documentTitle,
      signerName: currentRow.signer.displayName,
      signerCif: currentRow.signer.cifNumber,
    });

    const poll = window.setInterval(async () => {
      try {
        const s = await apiClient.retail.signing.presentation.get(pid);
        if (cancelled) return;

        // BACKLOG-1434 — surface the server's own refusal. This poll used to read
        // ONLY lastCapturedDocumentId, so when the server BLOCKED a ceremony (a
        // signer resolving to zero presentable documents) the officer's screen kept
        // spinning as if nothing were wrong. Two server signals now stop the room:
        //   packageSignableCount === 0 → the package has nothing signable at all
        //   lastError                  → the server refused this signer's step
        if (s.packageSignableCount === 0) {
          window.clearInterval(poll);
          setBlockedReason(
            'This application has no signable documents. Do not continue — return to Review and re-submit the application to rebuild the signing package.',
          );
          return;
        }
        if (s.lastError) {
          window.clearInterval(poll);
          setBlockedReason(s.lastError);
          return;
        }

        // The server ends the presentation itself once the cart's required signatures are
        // all in (Bill 2026-07-22). Finish the ceremony here too, instead of waiting forever
        // for a capture on a step the ended session will never show (Collette 2026-10-14).
        if (s.status === 'completed' && s.lastCapturedDocumentId !== docId) {
          window.clearInterval(poll);
          setCursor(plan ? plan.length : cursor);
          return;
        }

        if (s.lastCapturedDocumentId !== docId) return;
        window.clearInterval(poll);
        if (consentPending) {
          // Same pass mechanism the local EsignConsentGate sets — this
          // effect re-runs and pushes the signer's first capture step.
          setEsignPassed((prev) => {
            const next = new Set(prev);
            next.add(signerIndex);
            return next;
          });
        } else {
          setCompleted((prev) => {
            const next = new Set(prev);
            next.add(cursor);
            return next;
          });
          setCursor((c) => c + 1);
        }
      } catch {
        /* transient — next tick retries */
      }
    }, 1500);

    return () => {
      cancelled = true;
      window.clearInterval(poll);
    };
  }, [
    signerDisplay,
    currentRow,
    cursor,
    apiClient,
    skipEsignGate,
    currentSignerNeedsEsign,
    esignPassed,
  ]);

  function renderSignerDisplayPanel(r: PlanRow): JSX.Element {
    // Phase A — while the consent step is pushed, show a reading-status line.
    const consentOnKiosk =
      !skipEsignGate &&
      currentSignerNeedsEsign &&
      !esignPassed.has(r.signer.signerIndex);
    return (
      <div style={cardS}>
        <h2 style={headingS}>On the signer screen</h2>
        {consentOnKiosk ? (
          <p style={subHeadS}>
            Customer is reading the e-consent on the signer display… This
            screen advances automatically once{' '}
            <strong>{r.signer.displayName}</strong> agrees.
          </p>
        ) : (
          <p style={subHeadS}>
            <strong>{r.signer.displayName}</strong>{' '}
            {r.captureMode === 'review' ? 'is reviewing' : 'is signing'}{' '}
            <strong>{r.doc.documentTitle}</strong> on the signer display. This
            screen advances automatically once they{' '}
            {r.captureMode === 'review' ? 'acknowledge it' : 'finish'}.
          </p>
        )}
        {signerDisplay && (
          <div
            style={{
              display: 'flex',
              gap: 20,
              alignItems: 'center',
              flexWrap: 'wrap',
              margin: '6px 0',
            }}
          >
            {signerUrlQr && (
              <div
                aria-label="Scan to open the signer screen"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: signerUrlQr }}
              />
            )}
            <div>
              <div style={{ fontSize: 13, color: 'var(--ink-600)', marginBottom: 4 }}>
                Pairing code
              </div>
              <div
                style={{
                  fontSize: 28,
                  fontWeight: 700,
                  letterSpacing: 2,
                  color: 'var(--pcb-blue-dark)',
                }}
              >
                {signerDisplay.pairCode}
              </div>
              <div
                style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 6, maxWidth: 260 }}
              >
                Open <code>{signerPortalDisplay}</code> on the signer screen,
                or scan the code.
              </div>
            </div>
          </div>
        )}
        {reissueNote && (
          <div
            role="status"
            style={{
              fontSize: 12.5,
              color: 'var(--pcb-green)',
              background: 'var(--ok-bg)',
              border: '1px solid var(--ok-line)',
              borderRadius: 6,
              padding: '7px 10px',
              margin: '2px 0 4px',
            }}
          >
            {reissueNote}
          </div>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            style={{ ...ghostBtnS, opacity: reissuing ? 0.6 : 1 }}
            disabled={reissuing}
            onClick={() => void reissueSignerLink()}
            title="The signer's screen closed or lost the link — issue a fresh one for this same signing session."
          >
            {reissuing ? 'Reissuing link…' : 'Signer disconnected? Reissue link'}
          </button>
          <button
            type="button"
            style={ghostBtnS}
            onClick={() => void closeSignerDisplay()}
          >
            Bring back to this screen
          </button>
        </div>
      </div>
    );
  }

  function renderEsignGate(row: PlanRow): JSX.Element {
    return (
      <EsignConsentGate
        cartId={cartId}
        signerCifNumber={row.signer.cifNumber}
        signerEmail={row.signer.email}
        signerDisplayName={row.signer.displayName}
        onPass={handleEsignPassed}
        onCancel={() => onCancel?.()}
      />
    );
  }

  function renderInternalCapture(row: PlanRow): JSX.Element {
    if (row.captureMode === 'review') {
      return renderDisclosureReview(row);
    }
    if (row.captureMode === 'click') {
      return (
        <ClickToSignCapture
          signerName={row.signer.displayName}
          documentTitle={row.doc.documentTitle}
          disabled={submitting}
          onCapture={(r) => {
            void submitCapture(row, r);
          }}
          onCancel={() => onCancel?.()}
        />
      );
    }
    if (row.captureMode === 'typed') {
      return renderTypedCapture(row);
    }
    // Drawn mode — touch renders the canvas (the kiosk / FSR touchscreen).
    // Bill 2026-07-08 (0844): drawn is the in-branch DEFAULT, but a non-touch
    // FSR-inline fallback can't draw — degrade to TYPED (Bill's stated
    // fallback) rather than the /sign-mobile phone-QR, which is obsolete under
    // the Phase A kiosk. The kiosk itself is touch, so it always draws.
    if (isTouchDevice() || isTouchOnly) {
      return renderDrawnCapture(row);
    }
    return renderTypedCapture(row);
  }

  // View-only disclosure (Bill 2026-09-30): show it, record the acknowledgement.
  function renderDisclosureReview(row: PlanRow): JSX.Element {
    return (
      <DisclosureReviewCapture
        signerName={row.signer.displayName}
        documentTitle={row.doc.documentTitle}
        disabled={submitting}
        onCapture={(r) => {
          void submitCapture(row, r);
        }}
        onCancel={() => onCancel?.()}
      >
        {reviewDocBlob ? (
          <PdfCanvasViewer source={reviewDocBlob} title={row.doc.documentTitle} height="52vh" />
        ) : reviewDocError ? (
          <div style={bannerS('err')}>{reviewDocError}</div>
        ) : (
          <div style={bannerS('info')}>Loading the disclosure…</div>
        )}
      </DisclosureReviewCapture>
    );
  }

  function renderTypedCapture(row: PlanRow): JSX.Element {
    return (
      <TypedSignatureCapture
        signerFirstName={row.signer.firstName ?? row.signer.displayName.split(/\s+/)[0] ?? ''}
        signerLastName={row.signer.lastName ?? row.signer.displayName.split(/\s+/).slice(1).join(' ')}
        requiredLegalName={row.signer.displayName}
        documentTitle={row.doc.documentTitle}
        disabled={submitting}
        onCapture={(r) => {
          void submitCapture(row, r);
        }}
        onCancel={() => onCancel?.()}
      />
    );
  }

  function renderDrawnCapture(row: PlanRow): JSX.Element {
    // Drawn-signature canvas — Bill 2026-06-24 wired in the finished
    // DrawnSignatureCapture primitive (was a stub that emitted 'M0 0'/empty
    // PNG). Finger/stylus capture on a touchscreen, e.g. the FSR's ASUS
    // MB14AHD signer display. Real svgPath + PNG flow to /signing/internal/capture.
    return (
      <DrawnSignatureCapture
        signerName={row.signer.displayName}
        documentTitle={row.doc.documentTitle}
        disabled={submitting}
        isTouchOnly={isTouchOnly}
        onCapture={(r) => {
          void submitCapture(row, r);
        }}
        onCancel={() => onCancel?.()}
      />
    );
  }

  function renderDrawnDesktopFallback(row: PlanRow): JSX.Element {
    return (
      <div style={cardS}>
        <h2 style={headingS}>Finish this signature on your phone</h2>
        <p style={subHeadS}>
          {row.doc.documentTitle} needs a drawn signature. Scan the code below
          with your phone&apos;s camera, or open the link on a tablet — your
          progress will sync back here automatically.
        </p>
        <div style={qrWrapS}>
          {qrSvg ? (
            <div
              aria-label="QR code to continue signing on your phone"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: qrSvg }}
            />
          ) : (
            <div style={bannerS('info')}>Preparing your mobile link…</div>
          )}
          {mobileLinkUrl && (
            <a href={mobileLinkUrl} style={qrLinkS} target="_blank" rel="noreferrer">
              {mobileLinkUrl}
            </a>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" style={ghostBtnS} onClick={() => onCancel?.()}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  function renderSignNowEmbedded(row: PlanRow): JSX.Element {
    // CONTRACT(sibling-backend-core agent): EmbeddedSign already wires
    // /signing/create-embedded-invite + regenerate-embedded-invite-link via
    // the existing SignNow code path. Reused here.
    return (
      <div style={cardS}>
        <h2 style={headingS}>Signing online: {row.doc.documentTitle}</h2>
        <p style={subHeadS}>
          We&apos;ve opened a secure signing window for {row.signer.displayName}.
          When the signing window closes, this step will advance automatically.
        </p>
        <div style={bannerS('info')}>
          The SignNow signing window opens in a separate panel from your cart
          (sandboxed). Use the cart&apos;s <em>Signing</em> tab to monitor progress.
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" style={ghostBtnS} onClick={() => onCancel?.()}>
            Cancel
          </button>
          <button
            type="button"
            style={primaryBtnS}
            onClick={() => {
              // Optimistic — SignNow webhook will close the loop server-side.
              setCompleted((p) => {
                const n = new Set(p);
                n.add(cursor);
                return n;
              });
              setCursor((c) => c + 1);
            }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  function renderSignNowInvite(row: PlanRow): JSX.Element {
    return (
      <div style={cardS}>
        <h2 style={headingS}>Signing link sent to {row.signer.displayName}</h2>
        <p style={subHeadS}>
          We emailed {row.signer.email} a secure signing link for{' '}
          <strong>{row.doc.documentTitle}</strong>. When they finish, this
          cart will update on its own — no further action from you.
        </p>
        <div style={bannerS('ok')}>
          You can move to the next signer or close this view. The cart tracker
          will show when each signer completes.
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            style={primaryBtnS}
            onClick={() => {
              setCompleted((p) => {
                const n = new Set(p);
                n.add(cursor);
                return n;
              });
              setCursor((c) => c + 1);
            }}
          >
            Next signer
          </button>
        </div>
      </div>
    );
  }

  function renderWetAttested(row: PlanRow): JSX.Element {
    return (
      <WetSignScanner
        cartId={cartId}
        sessionId={cartId /* wet-attested reuses the cart id as session id today */}
        defaultSignerEmail={row.signer.email}
        defaultSignerName={row.signer.displayName}
        defaultSignerRole={`Signer ${row.signer.signerIndex + 1}`}
        {...(officerCode ? { officerCode } : {})}
        onComplete={(_summary: WetSignScanSummary) => {
          setCompleted((p) => {
            const n = new Set(p);
            n.add(cursor);
            return n;
          });
          setCursor((c) => c + 1);
        }}
        onCancel={() => onCancel?.()}
      />
    );
  }

  // ── Loading + error ─────────────────────────────────────────────────────

  if (planLoading) {
    return (
      <div style={containerS}>
        <div style={bannerS('info')}>Preparing your signing plan…</div>
      </div>
    );
  }

  if (planError) {
    return (
      <div style={containerS}>
        <div style={bannerS('err')}>
          We couldn&apos;t set up signing for this cart. {planError}
        </div>
      </div>
    );
  }

  // BACKLOG-1434 — the server refused this ceremony. Loud, terminal, and explicit
  // that nothing may be signed; the officer previously saw only a spinner.
  if (blockedReason) {
    return (
      <div style={containerS}>
        <div style={bannerS('err')}>
          <strong>Signing stopped — do not collect signatures.</strong>
          <div style={{ marginTop: 6 }}>{blockedReason}</div>
        </div>
      </div>
    );
  }

  if (!plan || plan.length === 0) {
    // 2026-08-03 incident: this was a WARN banner reading like a hint ("double-check
    // that the cart has…"), so a docless session looked like an ordinary empty state
    // and the ceremony proceeded around it. An empty plan means the customer would be
    // asked to sign nothing they can see — that is a stop, not a nudge.
    return (
      <div style={containerS}>
        <div style={bannerS('err')}>
          <strong>Signing stopped — no documents to present.</strong>
          <div style={{ marginTop: 6 }}>
            Do not collect signatures. Return to Review and re-submit the application
            to rebuild the signing package; if it stays empty, the cart&apos;s products
            or signers need to be corrected first.
          </div>
        </div>
      </div>
    );
  }

  if (cursor >= plan.length) {
    return (
      <div style={containerS}>
        <div style={bannerS('ok')}>
          All signatures captured. You can continue with the next step in the cart.
        </div>
      </div>
    );
  }

  const row = plan[cursor]!;
  const needsEsign =
    !skipEsignGate &&
    row.engine === 'internal' &&
    currentSignerNeedsEsign &&
    !esignPassed.has(row.signer.signerIndex);

  // Phase A — render-time mirror of the auto-open effect's condition so the
  // local consent gate / inline capture don't flash before the effect fires
  // or while presentation.create is in flight. Once the create resolves
  // (display open, or failed → fired-ref set + not opening) this settles.
  const kioskHandoffPending =
    presentationMode === 'kiosk-default' &&
    !signerDisplay &&
    (openingDisplay ||
      (!autoOpenFiredRef.current && plan.some((r) => r.engine === 'internal')));

  const percent = Math.round((cursor / plan.length) * 100);

  return (
    <div style={containerS}>
      <div>
        <div style={progressBarTrackS}>
          <div style={progressBarFillS(percent)} />
        </div>
        <div style={progressLabelS}>
          {cursor} of {plan.length} steps complete · {row.signer.displayName}{' '}
          {row.captureMode === 'review' ? 'reviewing' : 'signing'} <strong>{row.doc.documentTitle}</strong>
        </div>
      </div>

      {error && <div style={bannerS('err')}>{error}</div>}

      {/* Phase A — the local consent gate is the fsr-inline fallback ONLY:
          with a signer display open (or opening), consent runs on the kiosk
          via the pushed consent step instead. */}
      {needsEsign && !signerDisplay && !kioskHandoffPending && renderEsignGate(row)}

      {/* Ad-hoc method switch (Bill 2026-06-25): in-branch defaults to in-person
          capture; the FSR can flip THIS (signer × doc) to a remote SignNow link
          and back, on demand. Online-cart keeps its embedded-iframe surface. */}
      {!needsEsign && !kioskHandoffPending
        && (row.engine === 'internal' || (row.engine === 'signnow' && surface !== 'online-cart')) && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, fontSize: 12 }}>
          <span style={{ color: 'var(--ink-600)' }}>
            Method:{' '}
            <strong>{row.engine === 'internal' ? 'In person' : 'Remote link'}</strong>
          </span>
          <button
            type="button"
            style={{
              background: 'none', border: 'none', padding: 0, color: 'var(--pcb-blue)',
              fontSize: 12, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer',
            }}
            onClick={() => {
              const next: SigningEngine = row.engine === 'internal' ? 'signnow' : 'internal';
              setPlan((prev) =>
                prev ? prev.map((r, i) => (i === cursor ? { ...r, engine: next } : r)) : prev);
            }}
          >
            {row.engine === 'internal' ? 'Send a secure remote link instead →' : '← Switch back to in person'}
          </button>
        </div>
      )}

      {!needsEsign && !kioskHandoffPending && row.engine === 'internal' && !signerDisplay && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <button
            type="button"
            style={ghostBtnS}
            onClick={() => void openSignerDisplay()}
          >
            Open signer display
          </button>
        </div>
      )}
      {row.engine === 'internal' &&
        (signerDisplay ? (
          renderSignerDisplayPanel(row)
        ) : kioskHandoffPending ? (
          <div style={bannerS('info')}>Opening the signer display…</div>
        ) : !needsEsign ? (
          renderInternalCapture(row)
        ) : null)}
      {!needsEsign && row.engine === 'signnow' && surface === 'online-cart' &&
        renderSignNowEmbedded(row)}
      {!needsEsign && row.engine === 'signnow' && surface !== 'online-cart' &&
        renderSignNowInvite(row)}
      {!needsEsign && row.engine === 'wet-attested' && renderWetAttested(row)}
    </div>
  );
}

export default SigningSurfaceDispatcher;
